// Cart Recommendation Popup Event Handlers
// Class-based structure with event delegation for section re-renders

class CartRecommendationPopupHandler {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Event delegation for overlay click (close popup)
    document.addEventListener('click', (e) => this.handleOverlayClick(e));

    // Event delegation for close button click
    document.addEventListener('click', (e) => this.handleCloseButtonClick(e));

    // Event delegation for Escape key
    document.addEventListener('keydown', (e) => this.handleEscapeKey(e));

    // Event delegation for variant card selection
    document.addEventListener('click', (e) => this.handleVariantCardClick(e));

    // Event delegation for upgrade button click
    document.addEventListener('click', (e) => this.handleUpgradeButtonClick(e));
  }

  // Close popup
  closePopup(popup) {
    if (popup) {
      popup.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  // Reset upgrade button state
  resetUpgradeBtn(upgradeBtn) {
    if (upgradeBtn) {
      upgradeBtn.disabled = false;
      upgradeBtn.innerHTML = '<span class="cart-recommendation-popup__upgrade-btn-text xs-hide">Upgrade Cart</span><span class="cart-recommendation-popup__upgrade-btn-text xs-show">Add To Cart</span>';
    }
  }

  // Update current variant indicator in popup
  updateCurrentVariantIndicator(popup, newVariantId) {
    // Remove current class from all cards
    popup.querySelectorAll('.cart-recommendation-popup__variant-card').forEach(card => {
      card.classList.remove('cart-recommendation-popup__variant-card--current');
      const badge = card.querySelector('.cart-recommendation-popup__variant-badge');
      if (badge) badge.remove();
    });

    // Add current class to new variant card
    const newCurrentCard = popup.querySelector(`.cart-recommendation-popup__variant-card[data-variant-id="${newVariantId}"]`);
    if (newCurrentCard) {
      newCurrentCard.classList.add('cart-recommendation-popup__variant-card--current');

      // Add badge
      const badge = document.createElement('div');
      badge.className = 'cart-recommendation-popup__variant-badge';
      badge.textContent = 'Recommended';
      newCurrentCard.insertBefore(badge, newCurrentCard.firstChild);

      // Update radio to checked
      const radio = newCurrentCard.querySelector('.cart-recommendation-popup__variant-radio');
      if (radio) radio.checked = true;
    }

    // Update popup data attribute
    popup.dataset.currentVariantId = newVariantId;

    // Remove selected class from all cards
    popup.querySelectorAll('.cart-recommendation-popup__variant-card').forEach(c => {
      c.classList.remove('cart-recommendation-popup__variant-card--selected');
    });
  }

  // Get fresh line key from cart for current variant
  async getFreshLineKey(variantId) {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const item = cart.items.find(i => i.variant_id === parseInt(variantId, 10));
      return item ? item.key : null;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  // Handle overlay click
  handleOverlayClick(e) {
    if (e.target.classList.contains('cart-recommendation-popup__overlay')) {
      const popup = e.target.closest('.cart-recommendation-popup');
      this.closePopup(popup);
    }
  }

  // Handle close button click
  handleCloseButtonClick(e) {
    const closeBtn = e.target.closest('.cart-recommendation-popup__close-btn');
    if (closeBtn) {
      const popup = closeBtn.closest('.cart-recommendation-popup');
      this.closePopup(popup);
    }
  }

  // Handle Escape key
  handleEscapeKey(e) {
    if (e.key === 'Escape') {
      const activePopup = document.querySelector('.cart-recommendation-popup.active');
      if (activePopup) {
        this.closePopup(activePopup);
      }
    }
  }

  // Handle variant card click
  handleVariantCardClick(e) {
    const card = e.target.closest('.cart-recommendation-popup__variant-card');
    if (!card) return;

    const popup = card.closest('.cart-recommendation-popup');
    if (!popup) return;

    const radio = card.querySelector('.cart-recommendation-popup__variant-radio');
    if (radio && radio.disabled) return;

    // Remove selected class from all cards in this popup
    popup.querySelectorAll('.cart-recommendation-popup__variant-card').forEach(c => {
      c.classList.remove('cart-recommendation-popup__variant-card--selected');
    });

    // Add selected class to clicked card
    card.classList.add('cart-recommendation-popup__variant-card--selected');
    if (radio) radio.checked = true;

    // Update savings (if savings element exists)
    const savingsAmount = popup.querySelector('.savings-amount');
    const originalPriceEl = popup.querySelector('[data-original-price]');
    if (savingsAmount && originalPriceEl) {
      const originalPrice = parseInt(originalPriceEl.dataset.originalPrice) || 0;
      const newPrice = parseInt(card.dataset.variantPrice);
      const savings = Math.abs(originalPrice - newPrice);
      savingsAmount.textContent = (savings / 100).toFixed(0);
    }
  }

  // Handle upgrade button click
  async handleUpgradeButtonClick(e) {
    const upgradeBtn = e.target.closest('.cart-recommendation-popup__upgrade-btn');
    if (!upgradeBtn) return;

    const popup = upgradeBtn.closest('.cart-recommendation-popup');
    if (!popup) return;

    const selectedRadio = popup.querySelector('.cart-recommendation-popup__variant-radio:checked');
    if (!selectedRadio) return;

    const newVariantId = selectedRadio.value;
    const currentVariantId = popup.dataset.currentVariantId;
    const productId = popup.dataset.productId;

    if (newVariantId === currentVariantId) {
      this.closePopup(popup);
      return;
    }

    // Show loading state
    upgradeBtn.disabled = true;
    upgradeBtn.innerHTML = '<span class="cart-recommendation-popup__upgrade-btn-text xs-hide">Upgrade Cart</span><span class="cart-recommendation-popup__upgrade-btn-text xs-show">Updating...</span>';

    try {
      // Get fresh line key from cart
      const lineKey = await this.getFreshLineKey(currentVariantId);

      if (!lineKey) {
        console.error('Could not find item in cart');
        this.resetUpgradeBtn(upgradeBtn);
        return;
      }

      // Use the global upgradeCartVariant function
      if (typeof window.upgradeCartVariant === 'function') {
        const oldProductUrl = upgradeBtn?.dataset?.productUrl || '';
        await window.upgradeCartVariant(lineKey, newVariantId, oldProductUrl);

        // Update current variant indicator in popup
        this.updateCurrentVariantIndicator(popup, newVariantId);

        // Reset button state
        this.resetUpgradeBtn(upgradeBtn);

        // Remove upgrade-cart-container for this product
        const upgradeContainer = document.querySelector(`.upgrade-cart-container .upgrade-cart-popup-trigger[data-product-id="${productId}"]`);
        if (upgradeContainer) {
          const container = upgradeContainer.closest('.upgrade-cart-container');
          if (container) {
            container.remove();
          }
        }

        this.closePopup(popup);
      } else {
        // Fallback to page reload
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: 0 })
        });
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: newVariantId, quantity: 1})
        });
        window.location.reload();
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      this.resetUpgradeBtn(upgradeBtn);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartRecommendationPopupHandler = new CartRecommendationPopupHandler();
  });
} else {
  window.cartRecommendationPopupHandler = new CartRecommendationPopupHandler();
}
